import React, { useEffect, useState, useRef, lazy, createElement, Component } from 'react';
import { Share2, AlertTriangle, Shield, MessageSquare, TrendingDown, RefreshCw, Trash2, ArrowLeft, Check, X, ChevronDown, ChevronUp, Info, Sparkles, Activity } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { WebsiteHeader } from '../components/Dashboard/WebsiteHeader';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useScan, deleteScan } from '../hooks/useScans';
import { ShareableResultCard } from '../components/Analysis/ShareableResultCard';
import { SocialShareModal } from '../components/Analysis/SocialShareModal';
import { LoadingAnimation } from '../components/Analysis/LoadingAnimation';
import { NotificationBanner } from '../components/Notifications/NotificationBanner';
import { FindingCard } from '../components/Analysis/FindingCard';
import { TeaserResultCard } from '../components/Analysis/TeaserResultCard';
import { ReferralBanner } from '../components/Referral/ReferralBanner';
import { trackShare, trackCriticalEvent } from '../utils/mixpanelTracking';
import { trackAnalysisViewed, trackCheckoutStarted, trackResultDownloaded, trackResultShared, type ShareMethod } from '../utils/analytics';
import { trackEvent, trackEventOnce } from '../utils/visitorTracking';
import { useToast } from '../contexts/ToastContext';
import { useUpgradeModal } from '../contexts/UpgradeModalContext';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from '../contexts/AuthContext';
import { createPayment } from '../services/paymentService';
import { generateCaseFilePDF } from '../utils/pdfGenerator';
// --- Types ---
type TabType = 'overview' | 'patterns' | 'evidence';
type AspectRatio = 'square' | 'story';
// Social Media Caption Templates
const SOCIAL_CAPTIONS = {
  instagram_story: `I didn't expect this result.

Some communication patterns aren't obvious until you see them clearly.

🔍 Analyzed with ToxicDetector.ai`,
  instagram_feed: `This conversation showed concerning communication patterns I hadn't noticed before.

Tools like this don't judge — they help you see patterns more clearly.

🧠 Made with ToxicDetector.ai`,
  tiktok: `Some red flags don't look like red flags at first.

This analysis broke it down clearly.

#Communication #Relationships #ToxicDetector`,
  twitter: `This conversation showed concerning communication patterns I didn't catch on my own.

Insight matters.

Analyzed with ToxicDetector.ai`,
  youtube: `Some communication issues are subtle — but patterns tell a story.

This chat analysis highlighted behaviors worth paying attention to.

Made with ToxicDetector.ai`,
  facebook: `This conversation showed concerning communication patterns I hadn't noticed before.

Tools like this don't judge — they help you see patterns more clearly.

🧠 Made with ToxicDetector.ai`
};
export function AnalysisPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const scanId = searchParams.get('id');
  const {
    scan,
    loading,
    error,
    refetch,
    statusChange,
    clearStatusChange
  } = useScan(scanId || undefined);
  const {
    user
  } = useAuth();
  const {
    showToast
  } = useToast();
  // State
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareAspectRatio, setShareAspectRatio] = useState<AspectRatio>('story');
  const [shareCardTheme, setShareCardTheme] = useState<'dark' | 'light'>('dark');
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [unlockLoading, setUnlockLoading] = useState(false);
  // Refs for image capture
  const squareCaptureRef = useRef<HTMLDivElement>(null);
  const storyCaptureRef = useRef<HTMLDivElement>(null);
  const {
    openUpgradeModal
  } = useUpgradeModal();
  // --- Effects ---
  // A finished-but-locked scan means the free scan is done: offer the deeper
  // analysis once per scan, after the teaser has had a moment to land.
  useEffect(() => {
    if (!scan || scan.status !== 'completed' || scan.is_unlocked) return;
    const key = `td_free_scan_prompt_${scan.id}`;
    try {
      if (window.sessionStorage.getItem(key) === '1') return;
      window.sessionStorage.setItem(key, '1');
    } catch {
      return;
    }
    const timer = window.setTimeout(() => openUpgradeModal('free_scan_complete'), 1600);
    return () => window.clearTimeout(timer);
  }, [scan?.id, scan?.status, scan?.is_unlocked, openUpgradeModal]);
  // Handle scan status changes with toast notifications
  useEffect(() => {
    if (statusChange) {
      const {
        currentStatus,
        scan: changedScan
      } = statusChange;
      if (currentStatus === 'completed') {
        // The Smart Feedback provider receives one event only after this result page
        // has rendered, preventing duplicate counters and premature prompts.
        const completionKey = `smart_feedback_completed_scan_${changedScan.id}`;
        if (localStorage.getItem(completionKey) !== 'true') {
          requestAnimationFrame(() => {
            window.setTimeout(() => {
              window.dispatchEvent(new CustomEvent('scanCompleted'));
              localStorage.setItem(completionKey, 'true');
            }, 300);
          });
        }
        // Show success toast with score info
        const riskText = changedScan.risk_level === 'high' ? '⚠️ High risk patterns detected' : changedScan.risk_level === 'medium' ? '⚡ Some patterns to review' : '✅ Communication looks healthy';
        showToast(`Analysis Complete! Score: ${changedScan.score}/100. ${riskText}`, 'success', 5000);
      } else if (currentStatus === 'failed') {
        showToast('Analysis failed. Please try again or contact support if the issue persists.', 'error', 6000);
      }
      // Clear the status change after handling
      clearStatusChange();
    }
  }, [statusChange, clearStatusChange, showToast]);
  useEffect(() => {
    if (scan && scan.status === 'completed' && !showNotification) {
      setShowNotification(true);
    }
  }, [scan]);
  // Track analysis view on mount
  useEffect(() => {
    if (scan && scan.status === 'completed' && user?.id) {
      const timeSinceCompletion = scan.updated_at ? Math.floor((Date.now() - new Date(scan.updated_at).getTime()) / 1000) : 0;
      trackCriticalEvent('Analysis_Viewed', {
        scan_id: scan.id,
        toxicity_score: scan.score || 0,
        risk_level: scan.risk_level || 'unknown',
        patterns_count: Array.isArray(scan.patterns) ? scan.patterns.length : 0,
        is_unlocked: scan.is_unlocked,
        time_since_completion: timeSinceCompletion
      });
      trackAnalysisViewed(scan.risk_level || undefined);
      // First-party: only once the completed result has actually rendered.
      // Keyed on the scan id so Strict Mode, re-renders, or a refresh cannot
      // record the same result view twice.
      void trackEventOnce(`result_viewed:${scan.id}`, 'result_viewed', {
        scan_id: scan.id,
        score: scan.score ?? null,
        risk_level: scan.risk_level ?? null,
        is_unlocked: scan.is_unlocked
      });
    }
  }, [scan?.id, scan?.status, user?.id]);
  // --- Handlers ---
  const handleBack = () => navigate('/dashboard');
  const handleTryAgain = () => navigate('/upload');
  const handleUnlock = async () => {
    if (!scanId) return;
    try {
      setUnlockLoading(true);
      // Track unlock attempt
      await trackCriticalEvent('Unlock_Attempt', {
        scan_id: scanId,
        source: 'locked_analysis_page',
        score: scan?.score || 0,
        risk_level: scan?.risk_level
      });
      trackCheckoutStarted('single_scan', 'locked_analysis_page');
      // First-party: the user is opening checkout from the locked result.
      void trackEvent('premium_checkout_started', {
        plan: 'single_scan',
        location: 'locked_analysis_page',
        scan_id: scanId
      });
      // Create payment session with scan_id for proper unlock after payment
      const {
        authorization_url
      } = await createPayment('single_scan', {
        scanId
      });
      // Redirect to payment
      window.location.href = authorization_url;
    } catch (error) {
      console.error('Unlock error:', error);
      showToast('Payment initialization failed. Please try again.', 'error');
      setUnlockLoading(false);
    }
  };
  const handleDelete = async () => {
    if (!scanId) return;
    try {
      setDeleting(true);
      // Track deletion
      const daysSinceCreation = scan?.created_at ? Math.floor((Date.now() - new Date(scan.created_at).getTime()) / (1000 * 60 * 60 * 24)) : 0;
      await trackCriticalEvent('Scan_Deleted', {
        scan_id: scanId,
        toxicity_score: scan?.score || 0,
        days_since_creation: daysSinceCreation,
        was_completed: scan?.status === 'completed'
      });
      await deleteScan(scanId);
      showToast('Scan deleted successfully', 'success');
      navigate('/dashboard');
    } catch (error) {
      console.error('Error deleting scan:', error);
      showToast('Failed to delete scan. Please try again.', 'error');
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };
  const handleRetry = async () => {
    if (!scanId) return;
    try {
      setRetrying(true);
      // Track retry
      await trackCriticalEvent('Scan_Retry_Initiated', {
        scan_id: scanId,
        original_failure_reason: 'user_initiated_retry'
      });
      const {
        error
      } = await supabase.from('scans').update({
        status: 'pending',
        analysis: null
      }).eq('id', scanId);
      if (error) throw error;
      showToast('Retry initiated! AI analysis is processing again...', 'success');
      refetch();
    } catch (error) {
      console.error('Error retrying analysis:', error);
      showToast('Failed to retry analysis. Please try again.', 'error');
    } finally {
      setRetrying(false);
    }
  };
  const handleDownloadImage = async () => {
    if (!scanId) return;
    setIsGeneratingImage(true);
    try {
      const element = shareAspectRatio === 'square' ? squareCaptureRef.current : storyCaptureRef.current;
      if (!element) throw new Error('Capture element not found');
      const {
        toPng
      } = await import('html-to-image');
      const dataUrl = await toPng(element, {
        quality: 1,
        pixelRatio: 2
      });
      const link = document.createElement('a');
      link.download = `toxic-report-${scanId}-${shareAspectRatio}.png`;
      link.href = dataUrl;
      link.click();
      showToast('Image downloaded successfully!', 'success');
      // Track download
      const userPlan = user?.user_metadata?.subscriptionPlan || 'free';
      trackResultDownloaded('png');
      await trackShare('Image_Download', scan?.score || 0, userPlan);
    } catch (error) {
      console.error('Error downloading image:', error);
      showToast('Failed to generate image. Please try again.', 'error');
    } finally {
      setIsGeneratingImage(false);
    }
  };
  const handleDownloadPDF = async () => {
    if (!scanId || !scan) return;
    setIsGeneratingPDF(true);
    try {
      const analysisData = scan.analysis as {
        summary?: string;
        findings?: Array<{
          pattern: string;
          severity: string;
          example: string;
        }>;
        recommendations?: string[];
        messages?: any[];
        [key: string]: any;
      } | null;
      // Resolve risk level from multiple sources (matching UI display logic):
      // 1. scan.risk_level from DB
      // 2. analysis.risk_level (authoritative from backend)
      // 3. Derive from score as final fallback
      const resolvedScore = scan.score || 0;
      const resolvedRiskLevel = scan.risk_level || analysisData?.risk_level || (resolvedScore >= 70 ? 'high' : resolvedScore >= 40 ? 'medium' : 'low');
      // Pass ALL data sources so the PDF generator can resolve the best one:
      // - findings from analysis (if Edge Function returned structured findings)
      // - analysis object (for messages[], summary, recommendations)
      // - patterns from scan (fallback)
      await generateCaseFilePDF({
        scanId: scan.id,
        score: resolvedScore,
        riskLevel: resolvedRiskLevel,
        messageCount: scan.message_count || 0,
        summary: analysisData?.summary,
        findings: analysisData?.findings || [],
        recommendations: analysisData?.recommendations || getRecommendations(resolvedRiskLevel),
        createdAt: scan.created_at,
        patterns: scan.patterns || [],
        analysis: analysisData
      });
      showToast('PDF Case File downloaded successfully!', 'success');
      // Track download
      const userPlan = user?.user_metadata?.subscriptionPlan || 'free';
      trackResultDownloaded('pdf');
      await trackShare('PDF_Download', scan.score || 0, userPlan);
    } catch (error) {
      console.error('Error generating PDF:', error);
      showToast('Failed to generate PDF. Please try again.', 'error');
    } finally {
      setIsGeneratingPDF(false);
    }
  };
  const handleSocialShare = async (platform: 'instagram' | 'facebook' | 'tiktok' | 'twitter' | 'youtube' | 'whatsapp') => {
    const shareUrl = `${window.location.origin}/results?id=${scanId}&ref=share&score=${scan?.score || 0}`;
    const SHARE_IMAGE_URL = 'https://cdn.magicpatterns.com/uploads/dTFGJHFfyVyrJCztpQttwz/file_00000000e69c7243bed09cbc104eb039.png';
    let caption = '';
    switch (platform) {
      case 'instagram':
        caption = SOCIAL_CAPTIONS.instagram_feed;
        break;
      case 'tiktok':
        caption = SOCIAL_CAPTIONS.tiktok;
        break;
      case 'twitter':
        caption = SOCIAL_CAPTIONS.twitter;
        break;
      case 'youtube':
        caption = SOCIAL_CAPTIONS.youtube;
        break;
      case 'facebook':
        caption = SOCIAL_CAPTIONS.facebook;
        break;
      case 'whatsapp':
        caption = SOCIAL_CAPTIONS.twitter;
        break;
    }
    try {
      // Try native share with image if supported
      if (navigator.share) {
        try {
          const response = await fetch(SHARE_IMAGE_URL);
          const blob = await response.blob();
          const file = new File([blob], 'toxic-detector-result.png', {
            type: blob.type
          });
          if (navigator.canShare && navigator.canShare({
            files: [file]
          })) {
            await navigator.share({
              title: 'ToxicDetector Analysis',
              text: caption,
              url: shareUrl,
              files: [file]
            });
            // Track share — only after the native share actually resolved
            const userPlan = user?.user_metadata?.subscriptionPlan || 'free';
            const platformName = platform.charAt(0).toUpperCase() + platform.slice(1);
            trackResultShared('native_share', 'scan_result');
            // First-party: fires only after navigator.share actually resolved.
            void trackEvent('result_shared', {
              scan_id: scanId,
              platform,
              method: 'native_share',
              score: scan?.score ?? null
            });
            await trackShare(platformName, scan?.score || 0, userPlan);
            return;
          }
        } catch (e) {
          console.log('Native file share failed, falling back to text share', e);
        }
      }
      if (['instagram', 'tiktok', 'youtube'].includes(platform)) {
        const fullText = `${caption}\n\n${shareUrl}`;
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(fullText);
          let platformName = platform.charAt(0).toUpperCase() + platform.slice(1);
          showToast(`✓ Caption and link copied! Open ${platformName} and paste it.`, 'success');
        } else {
          const textArea = document.createElement('textarea');
          textArea.value = fullText;
          document.body.appendChild(textArea);
          textArea.select();
          document.execCommand('copy');
          document.body.removeChild(textArea);
          let platformName = platform.charAt(0).toUpperCase() + platform.slice(1);
          showToast(`✓ Caption and link copied! Open ${platformName} and paste it.`, 'success');
        }
      } else if (platform === 'whatsapp') {
        const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(caption + ' ' + shareUrl)}`;
        window.open(whatsappUrl, '_blank', 'width=600,height=400');
      } else if (platform === 'facebook') {
        const fbUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(caption)}`;
        window.open(fbUrl, '_blank', 'width=600,height=400');
      } else if (platform === 'twitter') {
        const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(caption)}&url=${encodeURIComponent(shareUrl)}`;
        window.open(twitterUrl, '_blank', 'width=600,height=400');
      }
      // Track share
      const userPlan = user?.user_metadata?.subscriptionPlan || 'free';
      const platformName = platform.charAt(0).toUpperCase() + platform.slice(1);
      trackResultShared(platform as ShareMethod, 'scan_result');
      // First-party: reached only once the copy/open share action succeeded.
      void trackEvent('result_shared', {
        scan_id: scanId,
        platform,
        method: ['instagram', 'tiktok', 'youtube'].includes(platform) ? 'copy_link' : 'web_intent',
        score: scan?.score ?? null
      });
      await trackShare(platformName, scan?.score || 0, userPlan);
    } catch (err) {
      console.error('Share error:', err);
      showToast('Unable to copy link. Please try again.', 'error');
    }
  };
  // --- Render Helpers ---
  if (!scanId) return <ErrorState message="No scan ID provided" onBack={handleBack} data-id="element-3244" data-mp="3fdu4fi0ckwsi" />;
  if (loading) return <LoadingState data-id="element-3245" data-mp="3agkgy7wiqv7b" />;
  if (error || !scan) return <ErrorState message={error || 'Scan not found'} onRetry={refetch} onBack={handleBack} data-id="element-3246" data-mp="35hhb4l8pskya" />;
  if (scan.status === 'processing' || scan.status === 'pending') return <ProcessingState onBack={handleBack} data-id="element-3247" data-mp="13p2wbv1gvwm3" />;
  if (scan.status === 'failed') return <FailedState onRetry={handleRetry} onBack={handleBack} retrying={retrying} data-id="element-3248" data-mp="1b9dgrsqzohsv" />;
  // LOCKED STATE - Use falsy check instead of strict equality
  // This handles null, undefined, and false values from the database
  if (scan.status === 'completed' && !scan.is_unlocked) {
    const score = scan.score || 0;
    // FIX: Use backend risk_level directly, but also derive from score as fallback
    const riskLevel = scan.risk_level || (score >= 70 ? 'high' : score >= 40 ? 'medium' : 'low');
    // DEBUG: Log scan data to verify backend values
    console.log('[AnalysisPage] Locked state:', {
      score: scan.score,
      risk: scan.risk_level,
      derivedRisk: riskLevel,
      patterns: scan.patterns
    });
    // FIX: Properly check for patterns array
    const hasPatterns = Array.isArray(scan.patterns) && scan.patterns.length > 0;
    const patterns = hasPatterns ? scan.patterns : [];
    return <div className="flex flex-col w-full min-h-screen bg-[#050505] font-sans text-white relative overflow-hidden" data-id="element-3249" data-mp="2il43u773g1zk">
        {/* Grid Background */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(20,20,20,0.5)_1px,transparent_1px),linear-gradient(90deg,rgba(20,20,20,0.5)_1px,transparent_1px)] bg-[size:40px_40px] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_50%,#000_70%,transparent_100%)] pointer-events-none fixed" data-id="element-3250" data-mp="1ufrfyti3roq" />

        <WebsiteHeader data-id="element-3251" data-mp="3a2e97gdvy26r" />

        <main className="flex-1 w-full max-w-4xl mx-auto px-4 sm:px-6 py-8 relative z-10" data-id="element-3252" data-mp="3a2wbour55cn4">
          <button onClick={handleBack} className="flex items-center gap-2 text-gray-500 hover:text-white mb-6 transition-colors group" data-id="element-3253" data-mp="3f0ft23j4y5ja">
            <ArrowLeft size={18} className="group-hover:-translate-x-1 transition-transform" data-id="element-3254" data-mp="3o1w50fplmfpm" />
            <span className="font-mono text-sm uppercase tracking-wider" data-id="element-3255" data-mp="ybk2wm3p82kj">
              Back to Dashboard
            </span>
          </button>

          {/* Use the new TeaserResultCard component */}
          <TeaserResultCard scanId={scan.id} messageCount={scan.message_count || 0} scanTitle={scan.title} score={score} riskLevel={riskLevel} patterns={patterns} onUnlockSuccess={() => refetch()} data-id="element-3256" data-mp="1dek3jck1cpf4" />
        </main>
      </div>;
  }
  // Data prep for UNLOCKED state
  const score = scan.score || 0;
  // FIX: Use backend risk_level directly, but derive from score as fallback
  // This ensures correct display even if DB risk_level is null
  const riskLevel = scan.risk_level || (score >= 70 ? 'high' : score >= 40 ? 'medium' : 'low');
  // DEBUG: Log scan data to verify backend values
  console.log('[AnalysisPage] Unlocked state:', {
    score: scan.score,
    risk: scan.risk_level,
    derivedRisk: riskLevel,
    patterns: scan.patterns,
    patternsType: typeof scan.patterns,
    patternsIsArray: Array.isArray(scan.patterns),
    analysis: scan.analysis
  });
  // FIX: Properly check for patterns array - handle both string[] and Pattern[]
  const hasPatterns = Array.isArray(scan.patterns) && scan.patterns.length > 0;
  const patterns = hasPatterns ? scan.patterns : [];
  // Extract analysis data for summary and findings
  const analysis = scan.analysis as {
    summary?: string;
    findings?: Array<{
      pattern: string;
      severity: string;
      example: string;
    }>;
    recommendations?: string[];
  } | null;
  // Get the AI-generated summary message from analysis
  const analysisSummary = analysis?.summary || null;
  const findings = analysis?.findings || [];
  return <div className="flex flex-col w-full min-h-screen bg-slate-50 font-sans text-slate-900" data-id="element-3257" data-mp="i7b46lm1l4cj">
      <WebsiteHeader data-id="element-3258" data-mp="n535g68ku2te" />

      {showNotification && <NotificationBanner message="Your analysis is ready to view" onClose={() => setShowNotification(false)} data-id="element-3259" data-mp="n7pds83akmco" />}

      <main className="flex-1 w-full max-w-4xl mx-auto px-4 sm:px-6 py-8" data-id="element-3260" data-mp="37w3nohytcuco">
        {/* Navigation */}
        <nav className="flex items-center justify-between mb-8" data-id="element-3261" data-mp="2zdrzdm6sgp2j">
          <button onClick={handleBack} className="group flex items-center gap-2 text-slate-500 hover:text-slate-800 transition-colors" data-id="element-3262" data-mp="2vd82mjq4nngx">
            <div className="p-2 rounded-full bg-white border border-slate-200 group-hover:border-slate-300 shadow-sm transition-all" data-id="element-3263" data-mp="39awo9ts0qyn1">
              <ArrowLeft size={18} data-id="element-3264" data-mp="2go23swv7bz9k" />
            </div>
            <span className="font-medium" data-id="element-3265" data-mp="2gold5p6sdfbz">Back to Dashboard</span>
          </button>

          <div className="flex items-center gap-2" data-id="element-3266" data-mp="2bqjrqxlw13xc">
            <button onClick={() => setShowDeleteConfirm(true)} className="p-2.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition-all" title="Delete Scan" data-id="element-3267" data-mp="2qdv5951mqy03">
              <Trash2 size={20} data-id="element-3268" data-mp="1xwslpz7d826d" />
            </button>
          </div>
        </nav>

        {/* Hero Section: Risk Score */}
        <section className="mb-10" data-id="element-3269" data-mp="1syh95gpordkg">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} className="bg-white rounded-3xl shadow-sm border border-slate-200 overflow-hidden relative" data-id="element-3270" data-mp="w7cd5w96cy9f">
            <div className={`absolute top-0 left-0 w-full h-1.5 ${getRiskColorBar(riskLevel)}`} data-id="element-3271" data-mp="1a0va7dup3euh" />

            <div className="p-8 md:p-10 flex flex-col md:flex-row items-center gap-10" data-id="element-3272" data-mp="1jwonx0k5hedu">
              {/* Score Circle */}
              <div className="relative flex-shrink-0" data-id="element-3273" data-mp="1ovrpgb0x92h2">
                <ScoreRing score={score} riskLevel={riskLevel} data-id="element-3274" data-mp="1ou70d0xdmbql" />
              </div>

              {/* Text Content */}
              <div className="flex-1 text-center md:text-left" data-id="element-3275" data-mp="3qno3brakaof0">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-100 text-slate-600 text-xs font-bold uppercase tracking-wider mb-4 border border-slate-200" data-id="element-3276" data-mp="3qphcpeck7mqp">
                  <Activity size={12} data-id="element-3277" data-mp="3n6rxqgx8sdk3" />
                  {scan.message_count} Messages Analyzed
                </div>

                <h1 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-3 leading-tight tracking-tight" data-id="element-3278" data-mp="3icxhxokrciqa">
                  {getRiskHeadline(riskLevel)}
                </h1>

                {/* NEW: Show AI-generated summary message if available */}
                {analysisSummary ? <p className="text-slate-600 text-lg leading-relaxed mb-6 max-w-xl" data-id="element-3279" data-mp="9wkt8kqkvx8e">
                    {analysisSummary}
                  </p> : <p className="text-slate-600 text-lg leading-relaxed mb-6 max-w-xl" data-id="element-3280" data-mp="wpe64twji33">
                    {getRiskDescription(riskLevel, patterns.length)}
                  </p>}

                {/* NEW: Show detected patterns summary in hero */}
                {patterns.length > 0 && <div className="flex flex-wrap gap-2 mb-6" data-id="element-3281" data-mp="2go3ie4whkpci">
                    {patterns.slice(0, 4).map((pattern, idx) => {
                  const patternType = typeof pattern === 'string' ? pattern : pattern.type || 'Unknown';
                  const patternSeverity = typeof pattern === 'string' ? 'MEDIUM' : pattern.severity || 'MEDIUM';
                  return <span key={idx} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wide ${getPatternBadgeColor(patternSeverity)}`} data-id="element-3282" data-mp="246izy35iek6a" data-mpi={idx}>
                          <AlertTriangle size={12} data-id="element-3283" data-mp="1uemz4kc1gfyd" />
                          {patternType}
                        </span>;
                })}
                    {patterns.length > 4 && <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium text-slate-500 bg-slate-100" data-id="element-3284" data-mp="11uhvjdmyql3e">
                        +{patterns.length - 4} more
                      </span>}
                  </div>}

                <div className="flex flex-wrap items-center justify-center md:justify-start gap-3" data-id="element-3285" data-mp="1asuuky1t3wk0">
                  <button onClick={() => setShowShareModal(true)} className="flex items-center gap-2 px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-semibold shadow-md hover:shadow-lg transition-all transform hover:-translate-y-0.5" data-id="element-3286" data-mp="16vyu32hz5vgt">
                    <Share2 size={18} data-id="element-3287" data-mp="1pkc59mbzjtjf" />
                    Share Result
                  </button>
                  <button onClick={handleTryAgain} className="flex items-center gap-2 px-6 py-3 bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 rounded-xl font-semibold transition-all" data-id="element-3288" data-mp="23jllgm06ynne">
                    <RefreshCw size={18} data-id="element-3289" data-mp="35wy3pc2m2r1g" />
                    Try Again
                  </button>
                </div>

                <p className="mt-6 text-xs text-slate-400 flex items-center justify-center md:justify-start gap-1.5 font-mono" data-id="element-3290" data-mp="3ay5bvc8o1s8n">
                  <Info size={12} data-id="element-3291" data-mp="3jxiky3svoesu" />
                  Based on communication patterns, not intent.
                </p>
              </div>
            </div>
          </motion.div>
        </section>

        {/* Comparison CTA Section */}
        <section className="mb-10" data-id="element-3292" data-mp="2r8uuzux6p18y">
          <div className="bg-gradient-to-r from-indigo-900 to-slate-900 rounded-3xl p-8 shadow-lg text-center relative overflow-hidden" data-id="element-3293" data-mp="2r5z0kgkjoup9">
            <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 mix-blend-overlay pointer-events-none" data-id="element-3294" data-mp="30yei5crxjh9m"></div>
            <div className="relative z-10" data-id="element-3295" data-mp="23gwf0xmsihph">
              <h3 className="text-2xl font-bold text-white mb-3" data-id="element-3296" data-mp="28gsc0cx2l8vj">
                Is this a pattern?
              </h3>
              <p className="text-indigo-200 mb-6 max-w-lg mx-auto" data-id="element-3297" data-mp="20xste9hfnld8">
                Compare these results with another conversation to see if you're
                dealing with a specific communication style or just a bad day.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-3298" data-mp="1mytf9vrjqovb">
                <button onClick={() => navigate('/upload')} className="w-full sm:w-auto px-6 py-3 bg-white text-indigo-900 rounded-xl font-bold hover:bg-indigo-50 transition-colors flex items-center justify-center gap-2" data-id="element-3299" data-mp="1hzgiojavi3fj">
                  <Activity size={18} data-id="element-3300" data-mp="187kjf4x1w79q" />
                  Compare with my Ex
                </button>
                <button onClick={() => navigate('/upload')} className="w-full sm:w-auto px-6 py-3 bg-indigo-800 text-white border border-indigo-700 rounded-xl font-bold hover:bg-indigo-700 transition-colors flex items-center justify-center gap-2" data-id="element-3301" data-mp="138hedkh2m11r">
                  <MessageSquare size={18} data-id="element-3302" data-mp="3a5qhgh6ohtb" />
                  Compare with my Bestie
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Tabs */}
        <div className="mb-8" data-id="element-3303" data-mp="d46303esopc2">
          <div className="flex p-1 bg-slate-100 rounded-xl gap-1 border border-slate-200" data-id="element-3304" data-mp="86mhqjjojcam">
            <TabButton active={activeTab === 'overview'} onClick={() => setActiveTab('overview')} data-id="element-3305" data-mp="3gmpo8lz22uxd">
              Overview
            </TabButton>
            <TabButton active={activeTab === 'patterns'} onClick={() => setActiveTab('patterns')} data-id="element-3306" data-mp="3qcseieeslus0">
              Detailed Patterns
            </TabButton>
            <TabButton active={activeTab === 'evidence'} onClick={() => setActiveTab('evidence')} data-id="element-3307" data-mp="2xwhqyki1eyp3">
              Evidence
            </TabButton>
          </div>
        </div>

        {/* Tab Content */}
        <AnimatePresence mode="wait" data-id="element-3308" data-mp="18eb2mpc3zb69">
          <motion.div key={activeTab} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.2
        }} data-id="element-3309" data-mp="18d9hfs1dp8at">
            {activeTab === 'overview' && <div className="space-y-6" data-id="element-3310" data-mp="1w4olbp8szxo6">
                {/* Patterns Summary */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3311" data-mp="1i55r80ctumuv">
                  {patterns.slice(0, 4).map((pattern, idx) => <PatternCard key={idx} pattern={pattern} index={idx} compact data-id="element-3312" data-mp="1m5pmszhr5iew" data-mpi={idx} />)}
                </div>

                {patterns.length === 0 && <EmptyState data-id="element-3313" data-mp="2de9bw6q08222" />}

                {/* Recommendations */}
                <div className="bg-gradient-to-br from-indigo-50 to-white rounded-2xl p-8 border border-indigo-100 shadow-sm" data-id="element-3314" data-mp="2ddhdyp8xps8m">
                  <h3 className="text-lg font-bold text-indigo-900 mb-6 flex items-center gap-2" data-id="element-3315" data-mp="23og1h91llsze">
                    <Sparkles className="text-indigo-600 w-5 h-5" data-id="element-3316" data-mp="1ypcvnmdsniqd" />
                    Recommendations
                  </h3>
                  <ul className="space-y-4" data-id="element-3317" data-mp="1uo1du315w1sq">
                    {getRecommendations(riskLevel).map((rec, i) => <li key={i} className="flex items-start gap-3 text-indigo-900/80" data-id="element-3318" data-mp="1pnn6yobaqxwa" data-mpi={i}>
                        <div className="mt-2 w-1.5 h-1.5 rounded-full bg-indigo-500 flex-shrink-0" data-id="element-3319" data-mp="1fqqsm1ypvhjt" />
                        <span className="leading-relaxed" data-id="element-3320" data-mp="3f3csgohces9e">{rec}</span>
                      </li>)}
                  </ul>
                </div>
              </div>}

            {activeTab === 'patterns' && <div className="space-y-4" data-id="element-3321" data-mp="3ikr0xebtyfl7">
                {findings.length > 0 ? <div className="grid grid-cols-1 gap-4" data-id="element-3322" data-mp="3mmcdeykgcruv">
                    {findings.map((item, idx) => <FindingCard key={`${item.pattern}-${idx}`} pattern={item.pattern} severity={item.severity} example={item.example} index={idx} data-id="element-3323" data-mp="3rf5f1dkmjk2q" data-mpi={idx} />)}
                  </div> : patterns.length > 0 ?
            // Fallback to patterns if no findings
            patterns.map((pattern, idx) => <PatternCard key={idx} pattern={pattern} index={idx} data-id="element-3324" data-mp="3dnh1d5uq8njn" data-mpi={idx} />) : <EmptyState data-id="element-3325" data-mp="nwlvczg0hmjj" />}
              </div>}

            {activeTab === 'evidence' && <EvidenceTab scan={scan} data-id="element-3326" data-mp="sudofwse7epb" />}
          </motion.div>
        </AnimatePresence>

        {/* Referral viral loop — after the report, never inside it */}
        <ReferralBanner tone="light" className="mt-12" data-id="element-3327" data-mp="1wuu5cpsp727x" />

        {/* Footer */}
        <footer className="mt-16 pt-8 border-t border-slate-200 text-center" data-id="element-3328" data-mp="2atlavnthvfkg">
          <p className="text-slate-500 text-sm mb-2 font-medium" data-id="element-3329" data-mp="2arh32ush5wew">
            Made with ToxicDetector.ai
          </p>
          <p className="text-slate-400 text-xs max-w-lg mx-auto leading-relaxed font-mono" data-id="element-3330" data-mp="2krrbm23kefnq">
            This analysis is based on communication patterns and does not
            diagnose intent or mental health conditions. For professional
            support, please consult a licensed therapist.
          </p>
        </footer>
      </main>

      {/* --- Modals & Overlays --- */}

      {/* Share Modal */}
      <SocialShareModal isOpen={showShareModal} onClose={() => setShowShareModal(false)} shareAspectRatio={shareAspectRatio} onAspectRatioChange={setShareAspectRatio} onSocialShare={handleSocialShare} onDownloadImage={handleDownloadImage} onDownloadPDF={handleDownloadPDF} isGeneratingImage={isGeneratingImage} isGeneratingPDF={isGeneratingPDF} score={score} riskLevel={riskLevel as 'low' | 'medium' | 'high'} messageCount={scan.message_count || 0} patterns={patterns} summary={analysisSummary} shareCardTheme={shareCardTheme} onThemeChange={setShareCardTheme} data-id="element-3331" data-mp="33hzdkgvj302c" />

      {/* Delete Confirmation */}
      <AnimatePresence data-id="element-3332" data-mp="346zph34bvrzz">
        {showDeleteConfirm && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm" data-id="element-3333" data-mp="2ph28b74tg2ka">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-xl" data-id="element-3334" data-mp="2uetwbq2cb4j7">
              <h3 className="text-lg font-bold text-slate-900 mb-2" data-id="element-3335" data-mp="m6yulfolsj8r">
                Delete Scan?
              </h3>
              <p className="text-slate-600 mb-6" data-id="element-3336" data-mp="h9gixhj7bpx2">
                This action cannot be undone. The analysis will be permanently
                removed.
              </p>
              <div className="flex gap-3" data-id="element-3337" data-mp="19xugxxaaim8m">
                <button onClick={() => setShowDeleteConfirm(false)} className="flex-1 py-2.5 bg-slate-100 text-slate-700 rounded-lg font-medium hover:bg-slate-200 transition-colors" data-id="element-3338" data-mp="19vi3nxfh901w">
                  Cancel
                </button>
                <button onClick={handleDelete} disabled={deleting} className="flex-1 py-2.5 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-70" data-id="element-3339" data-mp="vxjs0zsjjhq3">
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>

      {/* Hidden Capture Elements */}
      <div className="fixed -left-[9999px] top-0 pointer-events-none" data-id="element-3340" data-mp="255mzs9dl13rc">
        <div ref={squareCaptureRef} className={`w-[1080px] h-[1080px] ${shareCardTheme === 'dark' ? 'bg-[#0A0A0A]' : 'bg-[#FAFAFA]'}`} data-id="element-3341" data-mp="29x4uifp0i83v">
          <ShareableResultCard score={score} riskLevel={riskLevel} messageCount={scan.message_count || 0} topPatterns={patterns.slice(0, 3)} variant="square" summary={analysisSummary || undefined} theme={shareCardTheme} data-id="element-3342" data-mp="2eylpxvoq92d2" />
        </div>
        <div ref={storyCaptureRef} className={`w-[1080px] h-[1920px] ${shareCardTheme === 'dark' ? 'bg-[#0A0A0A]' : 'bg-[#FAFAFA]'}`} data-id="element-3343" data-mp="tjem4wqv0xof">
          <ShareableResultCard score={score} riskLevel={riskLevel} messageCount={scan.message_count || 0} topPatterns={patterns.slice(0, 4)} variant="vertical" summary={analysisSummary || undefined} theme={shareCardTheme} data-id="element-3344" data-mp="ykc2r4w81jnt" />
        </div>
      </div>
    </div>;
}
// --- Subcomponents ---
function ScoreRing({
  score,
  riskLevel,
  size = 'normal'
}: {
  score: number;
  riskLevel: string;
  size?: 'normal' | 'large';
}) {
  const radius = size === 'large' ? 80 : 58;
  const strokeWidth = size === 'large' ? 12 : 8;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - score / 100 * circumference;
  const width = size === 'large' ? 200 : 160;
  const getColor = () => {
    if (riskLevel === 'high') return '#ef4444'; // red-500
    if (riskLevel === 'medium') return '#f59e0b'; // amber-500
    return '#10b981'; // emerald-500
  };
  const getTextColor = () => {
    // In dark mode (locked state), we want white text for the score
    // In light mode (unlocked state), we want slate-900
    // We can infer mode from size since large is only used in locked state
    return size === 'large' ? 'text-white' : 'text-slate-900';
  };
  const getBgRingColor = () => {
    return size === 'large' ? 'text-gray-800' : 'text-slate-100';
  };
  return <div className={`relative flex items-center justify-center`} style={{
    width: width,
    height: width
  }} data-id="element-3345" data-mp="1fmufhnl4ct6o">
      <svg className="w-full h-full transform -rotate-90" data-id="element-3346" data-mp="23drj8d2rgk6u">
        {/* Background Ring */}
        <circle cx={width / 2} cy={width / 2} r={radius} stroke="currentColor" strokeWidth={strokeWidth} fill="transparent" className={getBgRingColor()} data-id="element-3347" data-mp="27f39mktgl9g3" />
        {/* Progress Ring */}
        <motion.circle initial={{
        strokeDashoffset: circumference
      }} animate={{
        strokeDashoffset: offset
      }} transition={{
        duration: 1.5,
        ease: 'easeOut'
      }} cx={width / 2} cy={width / 2} r={radius} stroke={getColor()} strokeWidth={strokeWidth} fill="transparent" strokeDasharray={circumference} strokeLinecap="round" data-id="element-3348" data-mp="2m9hme3mfjmmb" />
      </svg>

      <div className="absolute inset-0 flex flex-col items-center justify-center" data-id="element-3349" data-mp="2bxcme6k1pblf">
        <motion.span className={`${size === 'large' ? 'text-6xl' : 'text-5xl'} font-extrabold ${getTextColor()} tracking-tight`} initial={{
        opacity: 0,
        scale: 0.5
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.5,
        delay: 0.2
      }} data-id="element-3350" data-mp="2zle0rw3wnm8r">
          {score}
        </motion.span>
        <span className={`text-xs font-bold uppercase tracking-wider mt-1 px-2 py-0.5 rounded-full ${getRiskBadgeColor(riskLevel)}`} data-id="element-3351" data-mp="3naf454xvs776">
          {riskLevel} Risk
        </span>
      </div>
    </div>;
}
function PatternCard({
  pattern,
  index,
  compact = false
}: {
  pattern: any;
  index: number;
  compact?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  // FIX: Handle both string patterns and object patterns
  const isStringPattern = typeof pattern === 'string';
  const patternType = isStringPattern ? pattern : pattern.type || 'Unknown Pattern';
  const patternSeverity = isStringPattern ? 'MEDIUM' : pattern.severity || 'MEDIUM';
  const patternDescription = isStringPattern ? `${patternType} detected — open the Evidence tab to see the exact lines this pattern was based on.` : pattern.description || 'No description available';
  const patternCount = isStringPattern ? 1 : pattern.count || 1;
  const patternExamples = isStringPattern ? [] : pattern.examples || [];
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    delay: index * 0.1
  }} className={`bg-white rounded-xl border border-slate-200 overflow-hidden hover:border-indigo-200 transition-all hover:shadow-sm ${compact ? 'p-5' : 'p-6'}`} data-id="element-3352" data-mp="29a278hjjtp4p">
      <div className="flex items-start gap-4" data-id="element-3353" data-mp="2vb12fqovefnk">
        <div className={`p-3 rounded-xl flex-shrink-0 ${getPatternIconBg(patternSeverity)}`} data-id="element-3354" data-mp="2vasqq24hhgdy">
          {getPatternIcon(patternType)}
        </div>

        <div className="flex-1 min-w-0" data-id="element-3355" data-mp="22lu7f1omk872">
          <div className="flex items-center justify-between mb-2" data-id="element-3356" data-mp="22mwv70qnhuir">
            <h4 className="font-bold text-slate-900 truncate pr-2 text-lg tracking-tight" data-id="element-3357" data-mp="27koy0psu311q">
              {patternType}
            </h4>
            <span className={`text-[10px] font-bold px-2 py-1 rounded-full uppercase tracking-wide ${getPatternBadgeColor(patternSeverity)}`} data-id="element-3358" data-mp="2cejfrnovp5y6">
              {patternSeverity}
            </span>
          </div>

          <p className={`text-slate-600 leading-relaxed ${compact ? 'text-sm line-clamp-2' : 'text-base'}`} data-id="element-3359" data-mp="10rkr0ld4xfr">
            {patternDescription}
          </p>

          {!compact && !isStringPattern && <div className="mt-4" data-id="element-3360" data-mp="3o4o78otqlklm">
              <div className="flex items-center justify-between" data-id="element-3361" data-mp="3o7ttlwaze8dn">
                <span className="text-xs font-medium text-slate-400 font-mono" data-id="element-3362" data-mp="3j5upgk7f37fn">
                  {patternCount} instances detected
                </span>
                {patternExamples.length > 0 && <button onClick={() => setExpanded(!expanded)} className="text-sm font-medium text-indigo-600 hover:text-indigo-700 flex items-center gap-1 transition-colors" data-id="element-3363" data-mp="39g1hruaebcgo">
                    {expanded ? 'Hide Examples' : 'Show Examples'}
                    {expanded ? <ChevronUp size={16} data-id="element-3364" data-mp="2qhywnqkyxzo5" /> : <ChevronDown size={16} data-id="element-3365" data-mp="2qiqrv0anlvdw" />}
                  </button>}
              </div>

              <AnimatePresence data-id="element-3366" data-mp="zdxanwhr3rmm">
                {expanded && patternExamples.length > 0 && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} className="overflow-hidden" data-id="element-3367" data-mp="kp0vlfha1eci">
                    <div className="pt-4 space-y-2" data-id="element-3368" data-mp="1rpxhy7dsqyn">
                      {patternExamples.map((ex: string, i: number) => <div key={i} className="bg-slate-50 p-3 rounded-lg text-sm text-slate-700 border border-slate-100 italic" data-id="element-3369" data-mp="6r2s554hv2bs" data-mpi={i}>
                          "{ex}"
                        </div>)}
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>}
        </div>
      </div>
    </motion.div>;
}
function EvidenceTab({
  scan
}: {
  scan: any;
}) {
  const conversationText: string = (scan?.conversation_text || '').trim();
  const fileUrls: string[] = Array.isArray(scan?.file_urls) ? scan.file_urls : [];
  const analysis = scan?.analysis as {
    messages?: Array<{
      text?: string;
      severity?: number;
      flags?: string[];
      short_reason?: string;
    }>;
  } | null;
  const messages = Array.isArray(analysis?.messages) ? analysis.messages : [];
  const hasAnything = conversationText.length > 0 || fileUrls.length > 0 || messages.length > 0;
  if (!hasAnything) {
    return <div className="text-center py-12 bg-white rounded-2xl border border-slate-200 border-dashed" data-id="element-3370" data-mp="2jw7vdtiw9pqf">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-50 text-amber-600 mb-4" data-id="element-3371" data-mp="2eyphrptxmmc3">
          <Info size={32} data-id="element-3372" data-mp="iw2yo56reaqc" />
        </div>
        <h3 className="text-lg font-bold text-slate-900 mb-1" data-id="element-3373" data-mp="ns1jxjwsjcmj">
          No source content stored
        </h3>
        <p className="text-slate-500 max-w-md mx-auto" data-id="element-3374" data-mp="rtn0pgcg4b20">
          We don't have the original conversation on file for this scan. Run a
          new scan to see the raw evidence here.
        </p>
      </div>;
  }
  return <div className="space-y-6" data-id="element-3375" data-mp="11scbkp5xcv8r">
      {/* Pasted / typed conversation text */}
      {conversationText.length > 0 && <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-slate-200" data-id="element-3376" data-mp="1bidxpbepw3l1">
          <h3 className="text-lg font-bold text-slate-900 mb-2" data-id="element-3377" data-mp="1bkrrlgxnv9r0">
            Conversation You Submitted
          </h3>
          <p className="text-slate-500 text-sm mb-4" data-id="element-3378" data-mp="1kjc4u2kgcr7u">
            This is the exact text our analyzer received.
          </p>
          <pre className="whitespace-pre-wrap break-words bg-slate-50 border border-slate-200 rounded-xl p-4 text-sm text-slate-800 leading-relaxed font-mono max-h-96 overflow-y-auto" data-id="element-3379" data-mp="1pj8982x2aknx">
            {conversationText}
          </pre>
        </div>}
      {/* Uploaded screenshots */}
      {fileUrls.length > 0 && <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-slate-200" data-id="element-3380" data-mp="1pofv43foen5d">
          <h3 className="text-lg font-bold text-slate-900 mb-2" data-id="element-3381" data-mp="1lnnjj3jc065x">
            Screenshots You Uploaded ({fileUrls.length})
          </h3>
          <p className="text-slate-500 text-sm mb-4" data-id="element-3382" data-mp="2iefrfdw42rwm">
            Every file shown here was sent to the analyzer.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-id="element-3383" data-mp="28m9zu59ruchy">
            {fileUrls.map((url, i) => <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="block aspect-square rounded-xl overflow-hidden border border-slate-200 bg-slate-100 hover:border-indigo-300 transition-colors" data-id="element-3384" data-mp="24lzv0vxpts4o" data-mpi={i}>
                <img src={url} alt={`Screenshot ${i + 1}`} className="w-full h-full object-cover" loading="lazy" data-id="element-3385" data-mp="3fzdbd1cgm6t1" />
              </a>)}
          </div>
        </div>}
      {/* Per-message AI breakdown — only shown if backend returned messages */}
      {messages.length > 0 && <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-slate-200" data-id="element-3386" data-mp="2l8owmuyf4z1">
          <h3 className="text-lg font-bold text-slate-900 mb-2" data-id="element-3387" data-mp="7l4p0b0j6c9b">
            Per-Message Analysis ({messages.length})
          </h3>
          <p className="text-slate-500 text-sm mb-4" data-id="element-3388" data-mp="3os6vbc2upud8">
            Each line our analyzer extracted, with its severity and detected
            flags.
          </p>
          <div className="space-y-3" data-id="element-3389" data-mp="2dd1rg4qzhps1">
            {messages.map((msg, i) => {
          const sev = typeof msg.severity === 'number' ? msg.severity : 0;
          const sevColor = sev >= 70 ? 'bg-red-50 border-red-200 text-red-700' : sev >= 40 ? 'bg-amber-50 border-amber-200 text-amber-700' : 'bg-emerald-50 border-emerald-200 text-emerald-700';
          const flags = Array.isArray(msg.flags) ? msg.flags : [];
          return <div key={i} className="border border-slate-200 rounded-xl p-4 bg-slate-50/50" data-id="element-3390" data-mp="15evz030uupia" data-mpi={i}>
                  <div className="flex items-start justify-between gap-3 mb-2" data-id="element-3391" data-mp="cpyssy9x4j5w">
                    <p className="text-sm text-slate-800 leading-relaxed flex-1" data-id="element-3392" data-mp="qgtvfsttq411">
                      "{msg.text || '(empty line)'}"
                    </p>
                    <span className={`shrink-0 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full border ${sevColor}`} data-id="element-3393" data-mp="lok9sozqrq4k">
                      {sev}/100
                    </span>
                  </div>
                  {(flags.length > 0 || msg.short_reason) && <div className="flex flex-wrap items-center gap-1.5 mt-2" data-id="element-3394" data-mp="2n8nzikq2msg4">
                      {flags.map((f, j) => <span key={j} className="text-[10px] font-mono uppercase tracking-wide px-2 py-0.5 bg-white border border-slate-200 text-slate-600 rounded" data-id="element-3395" data-mp="2edzsoq4ul2p7" data-mpi={j}>
                          {f}
                        </span>)}
                      {msg.short_reason && <span className="text-xs text-slate-500 italic" data-id="element-3396" data-mp="3229c5lel9ed6">
                          — {msg.short_reason}
                        </span>}
                    </div>}
                </div>;
        })}
          </div>
        </div>}
    </div>;
}
function TabButton({
  active,
  children,
  onClick
}: {
  active: boolean;
  children: React.ReactNode;
  onClick: () => void;
}) {
  return <button onClick={onClick} className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-semibold transition-all ${active ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-200/50'}`} data-id="element-3397" data-mp="16o5c9g2n24nh">
      {children}
    </button>;
}
function EmptyState() {
  return <div className="text-center py-12 bg-white rounded-2xl border border-slate-200 border-dashed" data-id="element-3398" data-mp="1ld9uuydciaw4">
      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-50 text-green-600 mb-4" data-id="element-3399" data-mp="1pd247dakcl2j">
        <Check size={32} data-id="element-3400" data-mp="1ue7yvgnsaiep" />
      </div>
      <h3 className="text-lg font-bold text-slate-900 mb-1" data-id="element-3401" data-mp="1zb84xrkmvl5t">
        No Patterns Detected
      </h3>
      <p className="text-slate-500" data-id="element-3402" data-mp="24auhdysn0xdk">This conversation appears to be healthy.</p>
    </div>;
}
// --- Loading / Error States ---
function LoadingState() {
  return <div className="flex flex-col w-full min-h-screen bg-slate-50" data-id="element-3403" data-mp="4ztaixgogxjq">
      <WebsiteHeader data-id="element-3404" data-mp="9x38j1i4uhj6" />
      <div className="flex-1 flex items-center justify-center" data-id="element-3405" data-mp="9yef192yq3dz">
        <LoadingAnimation data-id="element-3406" data-mp="1wnc08vdzkpt" />
      </div>
    </div>;
}
function ProcessingState({
  onBack
}: {
  onBack: () => void;
}) {
  return <div className="flex flex-col w-full min-h-screen bg-slate-50" data-id="element-3407" data-mp="3t49fo1i3vn6o">
      <WebsiteHeader data-id="element-3408" data-mp="butccm1joisk" />
      <div className="flex-1 flex flex-col items-center justify-center p-6 text-center" data-id="element-3409" data-mp="bryo0jfeudtv">
        <LoadingAnimation status="processing" data-id="element-3410" data-mp="6tnds7l7e1b4" />
        <p className="mt-8 text-slate-600 font-medium animate-pulse" data-id="element-3411" data-mp="ku7xr72uu367">
          Analyzing conversation patterns...
        </p>
        <button onClick={onBack} className="mt-4 text-indigo-600 font-semibold hover:underline" data-id="element-3412" data-mp="fuvcurvjn669">
          Back to Dashboard
        </button>
      </div>
    </div>;
}
function ErrorState({
  message,
  onRetry,
  onBack
}: {
  message: string;
  onRetry?: () => void;
  onBack: () => void;
}) {
  return <div className="flex flex-col w-full min-h-screen bg-slate-50" data-id="element-3413" data-mp="13j4uripmz9s4">
      <WebsiteHeader data-id="element-3414" data-mp="zkxaidvyrqc6" />
      <div className="flex-1 flex flex-col items-center justify-center p-6 text-center" data-id="element-3415" data-mp="zlgs1thpccpo">
        <div className="w-16 h-16 bg-red-50 text-red-500 rounded-full flex items-center justify-center mb-4" data-id="element-3416" data-mp="1i8pph14cpxj9">
          <AlertTriangle size={32} data-id="element-3417" data-mp="1n4wwyycebh3b" />
        </div>
        <h2 className="text-xl font-bold text-slate-900 mb-2" data-id="element-3418" data-mp="1981b25b1pv5u">
          Something went wrong
        </h2>
        <p className="text-slate-500 mb-6 max-w-md" data-id="element-3419" data-mp="1e59ue97xk58o">{message}</p>
        <div className="flex gap-3" data-id="element-3420" data-mp="zhf2gv1qy4wn">
          {onRetry && <button onClick={onRetry} className="px-6 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700" data-id="element-3421" data-mp="zg3x4qsnkp3f">
              Retry
            </button>}
          <button onClick={onBack} className="px-6 py-2 bg-white border border-slate-200 text-slate-700 rounded-lg font-medium hover:bg-slate-50" data-id="element-3422" data-mp="qexf2dacv682">
            Dashboard
          </button>
        </div>
      </div>
    </div>;
}
function FailedState({
  onRetry,
  onBack,
  retrying
}: {
  onRetry: () => void;
  onBack: () => void;
  retrying: boolean;
}) {
  return <div className="flex flex-col w-full min-h-screen bg-slate-50" data-id="element-3423" data-mp="1nwwi4krc02tw">
      <WebsiteHeader data-id="element-3424" data-mp="1ju00aw9m8ar0" />
      <div className="flex-1 flex flex-col items-center justify-center p-6" data-id="element-3425" data-mp="1jvue7qmgz2ll">
        <div className="bg-white p-8 rounded-2xl shadow-sm border border-slate-200 max-w-md w-full text-center" data-id="element-3426" data-mp="1f0x7iqcnx5bv">
          <div className="w-16 h-16 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-3427" data-mp="1a1kko5lsjy9a">
            <X size={32} data-id="element-3428" data-mp="105hbifsiuzcu" />
          </div>
          <h2 className="text-xl font-bold text-slate-900 mb-2" data-id="element-3429" data-mp="104pnpr5625x4">
            Analysis Failed
          </h2>
          <p className="text-slate-500 mb-6" data-id="element-3430" data-mp="w4fjaix0vnkd">
            We encountered an issue processing your conversation.
          </p>
          <button onClick={onRetry} disabled={retrying} className="w-full py-3 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 disabled:opacity-70 mb-3 flex items-center justify-center gap-2" data-id="element-3431" data-mp="r6vwgum9e2gt">
            {retrying && <RefreshCw size={18} className="animate-spin" data-id="element-3432" data-mp="2t1tlytcqg9ru" />}
            {retrying ? 'Retrying...' : 'Retry Analysis'}
          </button>
          <button onClick={onBack} className="text-slate-500 font-medium hover:text-slate-800" data-id="element-3433" data-mp="2xz3am6sashem">
            Back to Dashboard
          </button>
        </div>
      </div>
    </div>;
}
// --- Helpers ---
function getRiskColorBar(level: string) {
  if (level === 'high') return 'bg-red-500';
  if (level === 'medium') return 'bg-amber-500';
  return 'bg-emerald-500';
}
function getRiskBadgeColor(level: string) {
  if (level === 'high') return 'bg-red-100 text-red-700';
  if (level === 'medium') return 'bg-amber-100 text-amber-700';
  return 'bg-emerald-100 text-emerald-700';
}
// FIX: Update headline copy to match toxicity semantics (higher score = more toxic = high risk)
function getRiskHeadline(level: string) {
  if (level === 'high') return 'High Risk Communication Pattern Detected';
  if (level === 'medium') return 'Potentially Concerning Communication';
  return 'No Major Toxic Patterns Detected';
}
function getRiskDescription(level: string, count: number) {
  if (level === 'high') return `We identified ${count} distinct patterns that may indicate unhealthy dynamics. It's important to review these carefully.`;
  if (level === 'medium') return `We found ${count} areas where communication could be improved. These patterns are common but worth addressing.`;
  return `Great news! We found ${count > 0 ? count + ' minor items' : 'no concerning patterns'}, and overall the communication style appears respectful and safe.`;
}
function getPatternBadgeColor(severity: string) {
  if (severity === 'HIGH') return 'bg-red-100 text-red-700';
  if (severity === 'MEDIUM') return 'bg-amber-100 text-amber-700';
  return 'bg-emerald-100 text-emerald-700';
}
function getPatternIconBg(severity: string) {
  if (severity === 'HIGH') return 'bg-red-50 text-red-600';
  if (severity === 'MEDIUM') return 'bg-amber-50 text-amber-600';
  return 'bg-emerald-50 text-emerald-600';
}
function getPatternIcon(type: string) {
  const map: Record<string, React.ReactNode> = {
    Gaslighting: <AlertTriangle size={20} data-id="element-3434" data-mp="2vmqwlp9zcqsz" />,
    Manipulation: <Shield size={20} data-id="element-3435" data-mp="2vo20dp3fdyk3" />,
    Dismissiveness: <MessageSquare size={20} data-id="element-3436" data-mp="2vkxtul2hmqq9" />,
    'Blame Shifting': <TrendingDown size={20} data-id="element-3437" data-mp="30mws5gjdexgc" />,
    'Emotional Abuse': <AlertTriangle size={20} data-id="element-3438" data-mp="3obg3y61mpsd6" />
  };
  return map[type] || <AlertTriangle size={20} data-id="element-3439" data-mp="3ocr5dz7lqo14" />;
}
function getRecommendations(level: string) {
  if (level === 'high') return ['Document these conversations for your own clarity.', 'Consider setting firm boundaries around acceptable language.', 'Trust your instincts if you feel unsafe or confused.', 'Seek support from a professional to navigate these dynamics.'];
  if (level === 'medium') return ['Try to address these patterns when you are both calm.', 'Use "I" statements to express how these messages make you feel.', 'Monitor if these patterns become more frequent over time.', 'Focus on clear, direct communication to avoid misunderstandings.'];
  return ['Continue fostering open and respectful dialogue.', 'Celebrate the positive aspects of your communication.', 'Address small misunderstandings before they grow.', 'Keep checking in with each other regularly.'];
}